import React, { useState, useEffect } from "react";
import { Input, Button, Switch, Space, Typography, Spin, Tag, Collapse, InputNumber } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import api from "../../../core/axios/axiosInstance";
import { useDispatch } from "react-redux";
import { showToast } from "../../../core/data/redux/slices/ToastSlice";

const { Text, Title } = Typography;
const { TextArea } = Input;

const EMAIL_TYPES = [
  {
    type: "balance_warning",
    label: "Low Balance Warning",
    description: "Sent to company admin when credit balance drops below $5",
    variables: ["{{companyName}}", "{{balance}}"],
    showFrequency: true,
  },
  {
    type: "account_locked",
    label: "Account Locked Notification",
    description: "Sent when account is locked for 24 hours due to failed login attempts. Include {{magicLink}} for the unlock button.",
    variables: ["{{name}}", "{{magicLink}}"],
    showFrequency: false,
  },
];

const SystemEmails = () => {
  const dispatch = useDispatch();
  const [templates, setTemplates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const [activationConfig, setActivationConfig]   = useState(null);
  const [activationLoading, setActivationLoading] = useState(true);
  const [activationSaving, setActivationSaving]   = useState(false);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/system-email-templates");
      const map = {};
      (res.data.data || []).forEach((t) => { map[t.type] = t; });
      setTemplates(map);
    } catch (err) {
      dispatch(showToast({ message: "Failed to load email templates", variant: "danger" }));
    } finally {
      setLoading(false);
    }
  };

  const fetchActivationConfig = async () => {
    setActivationLoading(true);
    try {
      const res = await api.get("/admin/activation-email-config");
      setActivationConfig(res.data.data);
    } catch (err) {
      dispatch(showToast({ message: "Failed to load activation email config", variant: "danger" }));
    } finally {
      setActivationLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
    fetchActivationConfig();
  }, [dispatch]);

  const handleChange = (type, field, value) => {
    setTemplates((prev) => ({
      ...prev,
      [type]: { ...prev[type], [field]: value },
    }));
  };

  const handleSave = async (type) => {
    setSaving((prev) => ({ ...prev, [type]: true }));
    try {
      await api.put(`/admin/system-email-templates/${type}`, templates[type]);
      dispatch(showToast({ message: "Template saved", variant: "success" }));
    } catch (err) {
      dispatch(showToast({ message: "Failed to save template", variant: "danger" }));
    } finally {
      setSaving((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handleActivationMasterChange = (field, value) => {
    setActivationConfig((prev) => ({ ...prev, [field]: value }));
  };

  const handleActivationEmailChange = (index, field, value) => {
    setActivationConfig((prev) => {
      const emails = [...prev.emails];
      emails[index] = { ...emails[index], [field]: value };
      return { ...prev, emails };
    });
  };

  const handleAddActivationEmail = () => {
    setActivationConfig((prev) => {
      const emails    = [...prev.emails];
      const warning   = emails.pop();
      const prevDelay = emails[emails.length - 1]?.delayDays || 0;
      const newDelayDays = Math.min(prevDelay + 7, warning.delayDays - 1);
      const newEntry  = {
        delayDays: newDelayDays,
        subject:   "VOYCELL: Reminder — Please activate your account",
        body:      `<p>Hi {{name}},</p><p>Your VOYCELL account is still not fully activated. Please log in and verify your phone number.</p><p>— The VOYCELL Team</p>`,
      };
      const allEmails = [...emails, newEntry, warning];
      return {
        ...prev,
        emails: allEmails.map((e, i) => ({ ...e, order: i + 1 })),
      };
    });
  };

  const handleRemoveActivationEmail = (index) => {
    setActivationConfig((prev) => {
      if (prev.emails.length <= 2) return prev;
      const emails = prev.emails
        .filter((_, i) => i !== index)
        .map((e, i) => ({ ...e, order: i + 1 }));
      return { ...prev, emails };
    });
  };

  const handleSaveActivationConfig = async () => {
    setActivationSaving(true);
    try {
      await api.put("/admin/activation-email-config", activationConfig);
      dispatch(showToast({ message: "Activation email config saved", variant: "success" }));
    } catch (err) {
      dispatch(showToast({
        message: err?.response?.data?.message || "Failed to save activation config",
        variant: "danger",
      }));
    } finally {
      setActivationSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="page-wrapper">
        <div className="content" style={{ display: "flex", justifyContent: "center", paddingTop: 60 }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="page-wrapper">
      <div className="content">
        <div style={{ marginBottom: 24 }}>
          <Title level={4} style={{ margin: 0 }}>System Emails</Title>
          <Text type="secondary">Configure automated email notifications sent by VOYCELL</Text>
        </div>

        <Collapse accordion style={{ marginBottom: 8 }}>
          {EMAIL_TYPES.map(({ type, label, description, variables, showFrequency }) => {
            const t = templates[type] || {};
            return (
              <Collapse.Panel
                key={type}
                header={
                  <Space>
                    <strong>{label}</strong>
                    {t.isActive === false && <Tag color="red">Disabled</Tag>}
                  </Space>
                }
              >
                <Space align="center" style={{ marginBottom: 12 }}>
                  <Text strong>Enabled</Text>
                  <Switch
                    checked={t.isActive !== false}
                    onChange={(v) => handleChange(type, "isActive", v)}
                    checkedChildren="Active"
                    unCheckedChildren="Inactive"
                  />
                </Space>

                <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>{description}</Text>

                <div style={{ marginBottom: 16 }}>
                  <Text strong>Available Variables:</Text>
                  <Space style={{ marginTop: 6 }} wrap>
                    {variables.map((v) => <Tag key={v} color="blue">{v}</Tag>)}
                  </Space>
                </div>

                <div style={{ marginBottom: 16 }}>
                  <Text strong>Subject</Text>
                  <Input
                    style={{ marginTop: 6 }}
                    value={t.subject || ""}
                    onChange={(e) => handleChange(type, "subject", e.target.value)}
                    placeholder="Email subject"
                  />
                </div>

                <div style={{ marginBottom: showFrequency ? 16 : 0 }}>
                  <Text strong>Body (HTML)</Text>
                  <TextArea
                    style={{ marginTop: 6, fontFamily: "monospace", fontSize: 12 }}
                    rows={8}
                    value={t.body || ""}
                    onChange={(e) => handleChange(type, "body", e.target.value)}
                    placeholder="Email body (HTML supported, use {{variableName}} for dynamic values)"
                  />
                </div>

                {showFrequency && (
                  <div style={{ marginBottom: 0 }}>
                    <Text strong>Send at most every (days)</Text>
                    <InputNumber
                      min={1}
                      style={{ marginTop: 6, width: 120 }}
                      value={t.reminderFrequencyDays ?? 7}
                      onChange={(val) => handleChange(type, "reminderFrequencyDays", val || 1)}
                    />
                  </div>
                )}

                <div style={{ marginTop: 16, textAlign: "right" }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={saving[type]}
                    onClick={() => handleSave(type)}
                  >
                    Save
                  </Button>
                </div>
              </Collapse.Panel>
            );
          })}
          <Collapse.Panel
            key="activation_reminder"
            header={
              <Space>
                <strong>Activation Reminder Emails</strong>
                {activationConfig && activationConfig.isActive === false && <Tag color="red">Disabled</Tag>}
              </Space>
            }
          >
            {activationLoading ? (
              <Spin />
            ) : activationConfig ? (
              <>
                <Space wrap style={{ marginBottom: 20 }}>
                  <Space>
                    <Text strong>Enabled</Text>
                    <Switch
                      checked={activationConfig.isActive}
                      onChange={(val) => handleActivationMasterChange("isActive", val)}
                      checkedChildren="Active"
                      unCheckedChildren="Inactive"
                    />
                  </Space>
                  <Space>
                    <Text strong>Suspension days after last email</Text>
                    <InputNumber
                      min={1}
                      value={activationConfig.suspensionDaysAfterLastEmail}
                      onChange={(val) => handleActivationMasterChange("suspensionDaysAfterLastEmail", val)}
                    />
                  </Space>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    loading={activationSaving}
                    onClick={handleSaveActivationConfig}
                  >
                    Save All
                  </Button>
                </Space>

                <Collapse accordion>
                  {activationConfig.emails.map((email, index) => {
                    const isLast = index === activationConfig.emails.length - 1;
                    return (
                      <Collapse.Panel
                        key={email.order}
                        header={
                          <Space>
                            <strong>
                              {`Email #${index + 1} — send after ${email.delayDays} day${email.delayDays !== 1 ? "s" : ""}`}
                              {isLast ? " · Final Warning (Suspension)" : ""}
                            </strong>
                            <Button
                              size="small"
                              danger
                              disabled={activationConfig.emails.length <= 2}
                              onClick={(e) => { e.stopPropagation(); handleRemoveActivationEmail(index); }}
                            >
                              Remove
                            </Button>
                          </Space>
                        }
                      >
                        <Space direction="vertical" style={{ width: "100%" }} size={12}>
                          <Space align="center">
                            <Text strong>Send after</Text>
                            <InputNumber
                              min={index === 0 ? 0 : (activationConfig.emails[index - 1]?.delayDays ?? 0) + 1}
                              value={email.delayDays}
                              onChange={(val) => handleActivationEmailChange(index, "delayDays", val)}
                              addonAfter="days from email verification"
                            />
                          </Space>

                          <div>
                            <Text strong>Subject</Text>
                            <Input
                              value={email.subject}
                              onChange={(e) => handleActivationEmailChange(index, "subject", e.target.value)}
                              style={{ marginTop: 6 }}
                            />
                          </div>

                          <div>
                            <Text strong>Body (HTML)</Text>
                            <TextArea
                              value={email.body}
                              rows={6}
                              onChange={(e) => handleActivationEmailChange(index, "body", e.target.value)}
                              style={{ marginTop: 6, fontFamily: "monospace", fontSize: 12 }}
                              placeholder="HTML body — use {{name}} and {{suspensionDate}} as variables"
                            />
                          </div>

                          <Space wrap>
                            <Text type="secondary" style={{ fontSize: 12 }}>Variables:</Text>
                            <Tag color="blue">{"{{name}}"}</Tag>
                            {isLast && <Tag color="orange">{"{{suspensionDate}}"}</Tag>}
                          </Space>

                          <div style={{ textAlign: "right" }}>
                            <Button
                              type="primary"
                              icon={<SaveOutlined />}
                              loading={activationSaving}
                              onClick={handleSaveActivationConfig}
                            >
                              Save
                            </Button>
                          </div>
                        </Space>
                      </Collapse.Panel>
                    );
                  })}
                </Collapse>

                <Button
                  type="dashed"
                  style={{ width: "100%", marginTop: 12 }}
                  onClick={handleAddActivationEmail}
                >
                  + Add Email (inserted before Final Warning)
                </Button>
              </>
            ) : null}
          </Collapse.Panel>
        </Collapse>

      </div>
    </div>
  );
};

export default SystemEmails;
